import React, { useEffect, useCallback } from "react";
import { Adalicon } from "../Icons.jsx";
import { Button } from "../../bootstrap/index.jsx";
import { FieldWithErrors } from "../Errors.jsx";
import { CELL_STATUS, FORM_STATUS } from "./constants.js";
import Table from "./Table.jsx";

const EditableTable = ({ columns: propColumns, status, cancel, submit, hideToolbar = false, ...rest }) => {
  const inEditMode = FORM_STATUS.EDIT == status || FORM_STATUS.ADD == status;

  const editableCols = useCallback((c) => ({
    classes: ({ row, isPlaceholder }) => {
      const status =
        (row.original
          ? row.original.rowStatus || row.original[`${c.editable}_status`]
          : CELL_STATUS.DISPLAY) || CELL_STATUS.DISPLAY;
      return `${typeof c.classes == "function" ? c.classes({ row }) : c.classes || ""} ${
        !isPlaceholder && inEditMode && c.editable && row.original && row.original[c.isEditableAttr || "editable"] !== false
          ? `editable-cell ${
              [CELL_STATUS.EDIT, CELL_STATUS.UPDATING].includes(status)
                ? `editing ${c.disableUndo ? "hide-undo" : ""}`
                : status == CELL_STATUS.ADD
                ? "add"
                : ""
            }`
          : ""
      }`;
    },
    Cell: (props) => (
      <EditableCell
        {...props}
        attr={c.editable}
        ViewElem={c.Cell}
        EditElem={c.EditableCell}
        enableSubmit={c.enableSubmit}
        disableUndo={c.disableUndo}
        isEditableAttr={c.isEditableAttr}
      />
    ),
  }));

  const columns = React.useMemo(
    () =>
      propColumns.map((c) => ({
        ...c,
        ...(c.editable || c.subColEditable
          ? {
              ...editableCols(c),
              ...(c.columns
                ? { columns: c.columns.map((sc) => ({ ...sc, ...editableCols(sc) })) }
                : {}),
            }
          : {}),
      })),
    [propColumns, inEditMode]
  );

  const displayToolbar = !hideToolbar && FORM_STATUS.DISPLAY != status;

  return (
    <>
      <Table columns={columns} {...rest} />
      {displayToolbar ? (
        <div className="form-toolbar" style={{ padding: "0.5rem 1rem" }}>
          <Button
            size="sm"
            className="pull-right mr-1"
            onClick={submit}
            disabled={[FORM_STATUS.CREATING, FORM_STATUS.UPDATING].includes(status)}
          >
            {[FORM_STATUS.CREATING, FORM_STATUS.UPDATING].includes(status)
              ? I18n.t("saving")
              : I18n.t("save")}
          </Button>
          <Button size="sm" type="default" className="pull-right mr-1" onClick={cancel}>
            {I18n.t("cancel")}
          </Button>
        </div>
      ) : null}
    </>
  );
};

const EditableCell = (props) => {
  const {
    row: { index, original },
    column,
    attr,
    disableEdit,
    ViewElem,
    EditElem,
    edit,
    cancelEdit,
    submit,
    setCellHeight = () => {},
    errors = {},
    enableSubmit = false,
    disableUndo = false,
    isEditableAttr = "editable",
  } = props;
  // rowStatus overrides individual cell statuses
  const status = original?.rowStatus || (original || {})[`${attr}_status`] || CELL_STATUS.DISPLAY;

  const handleEdit = (e) => {
    e.preventDefault();
    edit(index, attr);
  };

  const handleUndo = (e) => {
    e.preventDefault();
    cancelEdit(index, setCellHeight, attr);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    submit(index, attr);
  }

  useEffect(() => {
    // Reset cell height, when not in edit mode
    if (status == CELL_STATUS.DISPLAY || disableEdit) {
      setCellHeight(index, column.id);
    }
  }, [status, disableEdit]);

  return status == CELL_STATUS.DISPLAY || disableEdit ? (
    <>
      <ViewElem {...props} />
      {/* Only original rows may be editable, not grouped */}
      {!disableEdit && original && original[isEditableAttr] !== false ? (
        <a href="#" className="cell-action d-none" onClick={handleEdit}>
          <Adalicon type="edit" />
        </a>
      ) : null}
    </>
  ) : (
    <>
      <CellWithErrors
        errors={(errors[original.id] || {})[attr]}
        setCellHeight={(height) => setCellHeight(index, column.id, height)}
      >
        <EditElem {...props} disabled={status == CELL_STATUS.UPDATING} />
        <div className="cell-action-buttons d-inline-block pull-right">
          {enableSubmit ? (
            <Button
              elemType="button"
              size="xs"
              onClick={handleSubmit}
              disabled={status != CELL_STATUS.EDIT}
            >
              {status == CELL_STATUS.UPDATING ? I18n.t("loading") : I18n.t("save")}
            </Button>
          ) : null}
        </div>
      </CellWithErrors>
      {status == CELL_STATUS.EDIT && !disableUndo ? (
        <a href="#" className="cell-action" onClick={handleUndo}>
          <Adalicon type="undo" />
        </a>
      ) : null}
    </>
  );
};

const CellWithErrors = ({ setCellHeight, ...rest }) => {
  const rootRef = React.useRef();

  useEffect(() => {
    if (setCellHeight) {
      setCellHeight(rootRef.current ? rootRef.current.offsetHeight - 12 : undefined);
    }
  }, [rest.errors]);

  return <FieldWithErrors ref={rootRef} className="w-100" {...rest} />;
};

export default EditableTable;